/**
 * PDF 导出层。
 *
 * 设计原则：
 * 1. jsPDF 使用 mm 单位，确保页面和格线的几何尺寸有明确的物理意义；
 * 2. 大图按“单页 tile”逐页栅格化，避免手机一次创建超大 Canvas 耗尽内存；
 * 3. 格线用 PDF 矢量线条单独绘制，因此不会因为图片压缩而变糊；
 * 4. PDF 自己已经是正确尺寸，真正打印时仍必须选择 100% / Actual Size。
 */

import type { jsPDF as JsPDF } from 'jspdf'
import {
  calculateTilePlan,
  getCalibrationFactor,
  getImagePlacement,
  getPosterSizeMm,
  type Orientation,
} from './layout'

export interface PdfSettings {
  cols: number
  rows: number
  cellMm: number
  orientation: Orientation
  marginMm: number
  overlapMm: number
  zoom: number
  offsetXmm: number
  offsetYmm: number
  measured100Mm: number
  showGrid: boolean
  gridOpacity: number
  renderDpi: number
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('图片读取失败'))
    image.src = src
  })
}

function drawPageBackground(params: {
  image: HTMLImageElement
  placement: ReturnType<typeof getImagePlacement>
  tile: ReturnType<typeof calculateTilePlan>['tiles'][number]
  dpi: number
}) {
  const { image, placement, tile, dpi } = params
  const pxPerMm = dpi / 25.4
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(tile.widthMm * pxPerMm))
  canvas.height = Math.max(1, Math.round(tile.heightMm * pxPerMm))

  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) throw new Error('浏览器无法创建 Canvas 2D 上下文')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  const tileLeft = tile.xMm
  const tileTop = tile.yMm
  const tileRight = tile.xMm + tile.widthMm
  const tileBottom = tile.yMm + tile.heightMm
  const imageRight = placement.leftMm + placement.widthMm
  const imageBottom = placement.topMm + placement.heightMm

  // 求当前页面与图片在“成品毫米坐标”中的交集。
  const ix0 = Math.max(tileLeft, placement.leftMm)
  const iy0 = Math.max(tileTop, placement.topMm)
  const ix1 = Math.min(tileRight, imageRight)
  const iy1 = Math.min(tileBottom, imageBottom)

  if (ix1 > ix0 && iy1 > iy0) {
    const sx = ((ix0 - placement.leftMm) / placement.widthMm) * image.naturalWidth
    const sy = ((iy0 - placement.topMm) / placement.heightMm) * image.naturalHeight
    const sw = ((ix1 - ix0) / placement.widthMm) * image.naturalWidth
    const sh = ((iy1 - iy0) / placement.heightMm) * image.naturalHeight

    const dx = (ix0 - tileLeft) * pxPerMm
    const dy = (iy0 - tileTop) * pxPerMm
    const dw = (ix1 - ix0) * pxPerMm
    const dh = (iy1 - iy0) * pxPerMm

    ctx.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh)
  }

  return canvas
}

function drawVectorGrid(params: {
  doc: JsPDF
  tile: ReturnType<typeof calculateTilePlan>['tiles'][number]
  cols: number
  rows: number
  cellMm: number
  marginMm: number
  opacity: number
}) {
  const { doc, tile, cols, rows, cellMm, marginMm, opacity } = params
  const endX = cols * cellMm
  const endY = rows * cellMm
  const alpha = Math.min(1, Math.max(0.06, opacity))

  // jsPDF 的 GState 支持透明度；失败时仍会回退为普通黑色格线。
  try {
    doc.setGState(doc.GState({ opacity: alpha, 'stroke-opacity': alpha }))
  } catch {
    // 某些旧浏览器打包环境可能不支持 GState，不影响尺寸正确性。
  }

  for (let c = 0; c <= cols; c += 1) {
    const x = c * cellMm
    if (x < tile.xMm - 1e-9 || x > tile.xMm + tile.widthMm + 1e-9 || x > endX + 1e-9) continue
    const localX = marginMm + (x - tile.xMm)
    const major = c % 5 === 0
    doc.setDrawColor(major ? 50 : 95, major ? 56 : 100, major ? 65 : 108)
    doc.setLineWidth(major ? 0.22 : 0.09)
    doc.line(localX, marginMm, localX, marginMm + tile.heightMm)
  }

  for (let r = 0; r <= rows; r += 1) {
    const y = r * cellMm
    if (y < tile.yMm - 1e-9 || y > tile.yMm + tile.heightMm + 1e-9 || y > endY + 1e-9) continue
    const localY = marginMm + (y - tile.yMm)
    const major = r % 5 === 0
    doc.setDrawColor(major ? 50 : 95, major ? 56 : 100, major ? 65 : 108)
    doc.setLineWidth(major ? 0.22 : 0.09)
    doc.line(marginMm, localY, marginMm + tile.widthMm, localY)
  }

  try {
    doc.setGState(doc.GState({ opacity: 1, 'stroke-opacity': 1 }))
  } catch {
    // 同上。
  }
}

function drawCropMarks(doc: JsPDF, marginMm: number, widthMm: number, heightMm: number) {
  const mark = Math.min(4, Math.max(2, marginMm - 2))
  doc.setDrawColor(130, 136, 148)
  doc.setLineWidth(0.12)

  const left = marginMm
  const top = marginMm
  const right = marginMm + widthMm
  const bottom = marginMm + heightMm

  doc.line(left - mark, top, left, top)
  doc.line(left, top - mark, left, top)
  doc.line(right, top - mark, right, top)
  doc.line(right, top, right + mark, top)
  doc.line(left - mark, bottom, left, bottom)
  doc.line(left, bottom, left, bottom + mark)
  doc.line(right, bottom, right + mark, bottom)
  doc.line(right, bottom, right, bottom + mark)
}

function drawPageMeta(params: {
  doc: JsPDF
  pageWidthMm: number
  pageHeightMm: number
  marginMm: number
  pageIndex: number
  totalPages: number
  col: number
  row: number
  cellMm: number
  posterWidthMm: number
  posterHeightMm: number
}) {
  const {
    doc,
    pageWidthMm,
    pageHeightMm,
    marginMm,
    pageIndex,
    totalPages,
    col,
    row,
    cellMm,
    posterWidthMm,
    posterHeightMm,
  } = params

  // 窄边模式（例如 78×78 拼豆单张 A4）没有足够页边距容纳说明文字与标尺。
  // 此时优先保证模板本身完整，不让元信息盖在最外圈格子上。
  if (marginMm < 7) return

  doc.setTextColor(74, 80, 92)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)
  doc.text(
    `TrueScale | Page ${pageIndex + 1}/${totalPages} (${String.fromCharCode(65 + col)}${row + 1}) | Grid ${cellMm.toFixed(2)} mm | Final ${posterWidthMm.toFixed(1)} x ${posterHeightMm.toFixed(1)} mm`,
    marginMm,
    marginMm - 3,
  )
  doc.text('PRINT AT 100% / ACTUAL SIZE — disable Fit / Scale to page', marginMm, pageHeightMm - 3)

  // 100 mm 校准线：打印后用直尺量；若不是 100 mm，可把实测值输入网页进行补偿。
  const rulerWidth = Math.min(100, pageWidthMm - marginMm * 2 - 4)
  const rulerY = pageHeightMm - 7
  const rulerX = pageWidthMm - marginMm - rulerWidth
  doc.setDrawColor(35, 40, 48)
  doc.setLineWidth(0.25)
  doc.line(rulerX, rulerY, rulerX + rulerWidth, rulerY)
  for (let x = 0; x <= rulerWidth + 1e-9; x += 10) {
    const tick = x % 50 === 0 ? 3 : 1.7
    doc.line(rulerX + x, rulerY - tick, rulerX + x, rulerY + tick)
  }
  doc.setFontSize(6)
  doc.text(`${rulerWidth.toFixed(0)} mm scale`, rulerX, rulerY - 3.5)
}

export async function generatePrintPdf(params: {
  imageSrc: string
  settings: PdfSettings
}) {
  const { imageSrc, settings } = params
  // jsPDF 体积相对较大。只有用户真正导出时再加载，避免拖慢手机首屏和普通编辑操作。
  const { jsPDF } = await import('jspdf')
  const image = await loadHtmlImage(imageSrc)
  const factor = getCalibrationFactor(settings.measured100Mm)
  const effectiveCellMm = settings.cellMm * factor
  const poster = getPosterSizeMm(settings.cols, settings.rows, effectiveCellMm)
  const plan = calculateTilePlan({
    posterWidthMm: poster.widthMm,
    posterHeightMm: poster.heightMm,
    orientation: settings.orientation,
    marginMm: settings.marginMm,
    overlapMm: settings.overlapMm,
  })
  const placement = getImagePlacement({
    imageWidthPx: image.naturalWidth,
    imageHeightPx: image.naturalHeight,
    posterWidthMm: poster.widthMm,
    posterHeightMm: poster.heightMm,
    zoom: settings.zoom,
    offsetXmm: settings.offsetXmm * factor,
    offsetYmm: settings.offsetYmm * factor,
  })

  const doc = new jsPDF({
    orientation: settings.orientation,
    unit: 'mm',
    format: 'a4',
    compress: true,
    precision: 12,
  })

  for (let i = 0; i < plan.tiles.length; i += 1) {
    const tile = plan.tiles[i]
    if (i > 0) doc.addPage('a4', settings.orientation)

    const canvas = drawPageBackground({
      image,
      placement,
      tile,
      dpi: settings.renderDpi,
    })
    const jpeg = canvas.toDataURL('image/jpeg', 0.92)
    doc.addImage(
      jpeg,
      'JPEG',
      settings.marginMm,
      settings.marginMm,
      tile.widthMm,
      tile.heightMm,
      undefined,
      'FAST',
    )

    if (settings.showGrid) {
      drawVectorGrid({
        doc,
        tile,
        cols: settings.cols,
        rows: settings.rows,
        cellMm: effectiveCellMm,
        marginMm: settings.marginMm,
        opacity: settings.gridOpacity,
      })
    }

    drawCropMarks(doc, settings.marginMm, tile.widthMm, tile.heightMm)
    drawPageMeta({
      doc,
      pageWidthMm: plan.pageWidthMm,
      pageHeightMm: plan.pageHeightMm,
      marginMm: settings.marginMm,
      pageIndex: i,
      totalPages: plan.tiles.length,
      col: tile.col,
      row: tile.row,
      cellMm: settings.cellMm,
      posterWidthMm: settings.cols * settings.cellMm,
      posterHeightMm: settings.rows * settings.cellMm,
    })

    // 尽早释放每页 Canvas 引用，降低移动端连续导出时的峰值内存。
    canvas.width = 1
    canvas.height = 1
    // 不使用 requestAnimationFrame：后台标签页会暂停 rAF，可能让导出永远卡在“正在生成”。
    // 这里用极短的 setTimeout 让出事件循环，同样能降低连续大图处理对手机 UI 的阻塞。
    await new Promise<void>((resolve) => window.setTimeout(resolve, 0))
  }

  return {
    blob: doc.output('blob'),
    pageCount: plan.tiles.length,
    finalWidthMm: poster.widthMm,
    finalHeightMm: poster.heightMm,
  }
}

export async function generateCalibrationPdf() {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', precision: 12 })
  const x = 30
  const y = 80

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('TrueScale printer calibration', 30, 35)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Print at 100% / Actual Size. Disable Fit / Scale to page.', 30, 47)
  doc.text('Measure the line below with a physical ruler and enter the result back in the app.', 30, 54)

  doc.setDrawColor(20, 24, 31)
  doc.setLineWidth(0.35)
  doc.line(x, y, x + 100, y)
  for (let mm = 0; mm <= 100; mm += 1) {
    const tick = mm % 10 === 0 ? 5 : mm % 5 === 0 ? 3.2 : 1.6
    doc.line(x + mm, y - tick, x + mm, y + tick)
  }
  doc.setFontSize(12)
  doc.text('100.00 mm', x, y + 14)

  // 再放一个 50×50 mm 方框，方便同时检查横纵方向是否一致。
  doc.rect(x, y + 35, 50, 50)
  doc.setFontSize(9)
  doc.text('50 x 50 mm check square', x, y + 91)

  return doc.output('blob')
}
